import config from "console_config";
import { EnvTypeDataRetention } from "Reducers/projectSettings";

export const ANALYTICS_ENABLED = config.ID_GOOGLE_TAG_MANAGER;
export const PROJECT_ID_FIELD = "id";
export const ORGANIZATION_ID_FIELD = "name";
export const ENVIRONMENT_ID_FIELD = "id";
export const TEAM_ID_FIELD = "name";

export const CONTINUOUS_PROFILING_KEY = "continuous_profiling";

export const BILLING_PATH_MATCH =
  /^\/[A-z0-9-]+\/[A-z0-9-]+\/plan$|^\/-\/users\/[A-z0-9-]+\/billing/;
export const SETTINGS_PATH_MATCH =
  /^\/[A-z0-9-]+\/[A-z0-9-]+\/plan$|^\/-\/users\/[A-z0-9-]+\/settings/;
export const SUPPORT_PATH_MATCH =
  /^\/[A-z0-9-]+\/[A-z0-9-]+\/plan$|^\/-\/users\/[A-z0-9-]+\/tickets/;

export const PLAN_TYPES = {
  "trial/development": "Trial",
  development: "Development",
  essential: "Essential",
  small: "Small",
  standard: "Standard",
  "high-mem/standard": "Standard High Mem",
  "standard-high-memory": "Standard High Mem",
  medium: "Medium",
  "high-mem/medium": "Medium High Mem",
  "medium-high-memory": "Medium High Mem",
  large: "Large",
  "large-high-memory": "Large High Mem",
  "high-mem/large": "Large High Mem",
  xlarge: "Xlarge",
  "high-mem/xlarge": "X-Large High Mem",
  "xlarge-high-memory": "X-Large High Mem",
  "2xlarge": "2xlarge",
  "high-mem/2xlarge": "2X-Large High Mem",
  "2xlarge-high-memory": "2X-Large High Mem"
};

export const BACKUP_LABEL = {
  BACKUPS_ESSENTIAL: "BASIC",
  BACKUPS_BASIC: "BASIC",
  BACKUPS_ADVANCED: "PLATFORM_FEATURE_BACKUPS_ADVANCED",
  BACKUPS_PREMIUM: "PLATFORM_FEATURE_BACKUPS_PREMIUM"
};

// TODO remove all "ESSENTIAL" after BackEnd has replaced "ESSENTIAL" plan with "BASIC" plan
// The BackEnd is changing the name for the "Essential" plan to "Basic". To ensure no breaks, both cases are being considered.
const basicType = {
  label: "Basic",
  manual: 2,
  automated: 2
};
// There are two different "advanced" backups returned at different times, "ADVANCED", is a "default" type, and "PLATFORM_FEATURE_BACKUPS_ADVANCED", is a "sellable" type
const advancedType = {
  label: "Advanced",
  manual: 4,
  automated: 21
};

export const BACKUP_TYPES = {
  BASIC: basicType,
  ESSENTIAL: basicType,
  ADVANCED: advancedType,
  PLATFORM_FEATURE_BACKUPS_ADVANCED: advancedType,
  PLATFORM_FEATURE_BACKUPS_PREMIUM: {
    label: "Premium",
    manual: 4,
    automated: 44
  }
};

export const BACKUP_DESCRIPTIONS = {
  Basic:
    "Automated backups are stored for 2 days. You can store up to 2 manual backups at any time.",
  Advanced:
    "21 automated and 4 manual backups. {br}Each daily backup is stored for 1 week. {br}Each weekly backup is stored for 1 month. {br}Each monthly backup is stored for 1 year.",
  Premium:
    "44 automated backups and 4 manual backups. {br}Each 6-hourly backup is stored for 1 day. {br}Each daily backup is stored for 1 month. {br}Each monthly backup is stored for 1 year."
};

export const GIG = 1024;
export const STORAGE_SUFFIX = "GB";

// List of available environment types, we don't get them from the server
// so we need to hardcode them
export const ENVIRONMENT_TYPES: EnvTypeDataRetention[] = [
  "production",
  "staging",
  "development"
] as const;
export const DATE_TIME_FORMAT = "HH:mm MMM d, yyyy";

export const PERMISSIONS = [
  { value: "billing", label: "Manage billing" },
  { value: "plans", label: "Manage plans" },
  { value: "members", label: "Manage users" },
  { value: "projects:create", label: "Create projects" }
];

export const ACTIVITY_CONTEXT = {
  Environment: "environment",
  Integration: "integration",
  Project: "project",
  Crons: "crons"
} as const;

export const SUBSCRIPTION_PLAN = [
  // The order of the plan is important as in this case it is sorted in
  // ascending order of the plan size
  "ovh-team-4",
  "ovh-soho-1",
  "ovh-soho-2",
  "ovh-soho-4",
  "ovh-large-team-1",
  "ovh-large-team-2",
  "ovh-large-team-4",
  "ovh-large-team-8",
  "ovh-large-team-16",
  "ovh-soho-8",
  "ovh-team-1",
  "ovh-team-2",
  "ovh-team-8",
  "development",
  "trial/development",
  "small",
  "essential",
  "trial/standard",
  "standard",
  "high-mem/standard",
  "standard-high-memory",
  "trial/medium",
  "medium",
  "high-mem/medium",
  "medium-high-memory",
  "trial/large",
  "large",
  "high-mem/large",
  "large-high-memory",
  "xlarge",
  "high-mem/xlarge",
  "xlarge-high-memory",
  "trial/2xlarge",
  "2xlarge",
  "high-mem/2xlarge",
  "2xlarge-high-memory",
  "grid/xlarge",
  "4xlarge",
  "grid/2xlarge",
  "grid/4xlarge",
  "grid/8xlarge"
];

export const ACTIVITY_TYPES = Object.freeze({
  CDN_FASTLY_ORIGIN: {
    RESTRICTION: {
      DISABLE: "cdn.fastly.origin_restriction_disable",
      SETUP: "cdn.fastly.origin_restriction_setup"
    }
  },
  ENVIRONMENT: {
    ACCESS: {
      ADD: "environment.access.add",
      REMOVE: "environment.access.remove"
    },
    ACTIVATE: "environment.activate",
    BACKUP: "environment.backup",
    ALERT: "environment.alert",
    BACKUP_DELETE: "environment.backup.delete",
    BRANCH: "environment.branch",
    CERTIFICATE_RENEWAL: "environment.certificate.renewal",
    CRON: "environment.cron",
    DEACTIVATE: "environment.deactivate",
    DELETE: "environment.delete",
    DEPLOY: "environment.deploy",
    DOMAIN: {
      CREATE: "environment.domain.create",
      DELETE: "environment.domain.delete",
      UPDATE: "environment.domain.update"
    },
    INITIALIZE: "environment.initialize",
    MAINTENANCE_REDEPLOY: "environment.maintenance.redeploy",
    MERGE: "environment.merge",
    MERGE_PR: "environment.merge-pr",
    OPERATION: "environment.operation",
    PAUSE: "environment.pause",
    PUSH: "environment.push",
    REDEPLOY: "environment.redeploy",
    RESOURCES: { UPDATE: "environment.resources.update" },
    RESTORE: "environment.restore",
    RESUME: "environment.resume",
    ROUTE: {
      CREATE: "environment.route.create",
      DELETE: "environment.route.delete",
      UPDATE: "environment.route.update"
    },
    SOURCE_OPERATION: "environment.source-operation",
    SUBSCRIPTION: { UPDATE: "environment.subscription.update" },
    SYNCHRONIZE: "environment.synchronize",
    TASK: "environment.task",
    UPDATE: {
      HTTP_ACCESS: "environment.update.http_access",
      RESTRICT_ROBOTS: "environment.update.restrict_robots",
      SMTP: "environment.update.smtp"
    },
    VARIABLE: {
      CREATE: "environment.variable.create",
      DELETE: "environment.variable.delete",
      UPDATE: "environment.variable.update"
    }
  },
  INTEGRATION: {
    BITBUCKET: {
      FETCH: "integration.bitbucket.fetch",
      REGISTER_HOOKS: "integration.bitbucket.register_hooks",
      SERVER: {
        FETCH: "integration.bitbucket_server.fetch",
        REGISTER_HOOKS: "integration.bitbucket_server.register_hooks"
      }
    },
    BLACKFIRE: { WEBHOOK: "integration.blackfire.webhook" },
    GITHUB: {
      FETCH: "integration.github.fetch",
      REGISTER_HOOKS: "integration.github.register_hooks"
    },
    GITLAB: {
      FETCH: "integration.gitlab.fetch",
      REGISTER_HOOKS: "integration.gitlab.register_hooks"
    },
    HEALTH: {
      EMAIL: "integration.health.email",
      PAGERDUTY: "integration.health.pagerduty",
      SLACK: "integration.health.slack",
      WEBHOOK: "integration.health.webhook"
    },
    SCRIPT: "integration.script",
    WEBHOOK: "integration.webhook"
  },
  MAINTENANCE: { UPGRADE: "maintenance.upgrade" },
  PROJECT: {
    CLEAR_BUILD_CACHE: "project.clear_build_cache",
    CREATE: "project.create",
    SUSPEND: "project.suspend",
    RESUME: "project.resume",
    DOMAIN: {
      CREATE: "project.domain.create",
      DELETE: "project.domain.delete",
      UPDATE: "project.domain.update"
    },
    MODIFY_TITLE: "project.modify.title",
    VARIABLE: {
      CREATE: "project.variable.create",
      DELETE: "project.variable.delete",
      UPDATE: "project.variable.update"
    }
  }
} as const);

export const ACTIVITY_STATE = Object.freeze({
  CANCELLED: "cancelled",
  COMPLETE: "complete",
  IN_PROGRESS: "in_progress",
  PENDING: "pending",
  SCHEDULED: "scheduled",
  STAGED: "staged"
} as const);

export const activityTypeRun = [
  ACTIVITY_TYPES.ENVIRONMENT.OPERATION,
  ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION
];

export const activityTypeBuilds = [
  ACTIVITY_TYPES.ENVIRONMENT.BRANCH,
  ACTIVITY_TYPES.ENVIRONMENT.DEPLOY,
  ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE,
  ACTIVITY_TYPES.ENVIRONMENT.MERGE,
  ACTIVITY_TYPES.ENVIRONMENT.PUSH,
  ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY,
  ACTIVITY_TYPES.ENVIRONMENT.RESTORE,
  ACTIVITY_TYPES.ENVIRONMENT.ROUTE.CREATE,
  ACTIVITY_TYPES.ENVIRONMENT.ROUTE.DELETE,
  ACTIVITY_TYPES.ENVIRONMENT.ROUTE.UPDATE,
  ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.CREATE,
  ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.DELETE,
  ACTIVITY_TYPES.ENVIRONMENT.VARIABLE.UPDATE,
  ACTIVITY_TYPES.ENVIRONMENT.SYNCHRONIZE,
  ACTIVITY_TYPES.INTEGRATION.BITBUCKET.FETCH,
  ACTIVITY_TYPES.INTEGRATION.BITBUCKET.REGISTER_HOOKS,
  ACTIVITY_TYPES.INTEGRATION.BITBUCKET.SERVER.FETCH,
  ACTIVITY_TYPES.INTEGRATION.BITBUCKET.SERVER.REGISTER_HOOKS,
  ACTIVITY_TYPES.INTEGRATION.GITHUB.FETCH,
  ACTIVITY_TYPES.INTEGRATION.GITLAB.FETCH,
  ACTIVITY_TYPES.PROJECT.CREATE,
  ACTIVITY_TYPES.PROJECT.VARIABLE.CREATE,
  ACTIVITY_TYPES.PROJECT.VARIABLE.DELETE,
  ACTIVITY_TYPES.PROJECT.VARIABLE.UPDATE
];

export const INFRASTRUCTURE_TYPE_GRID = "grid";
export const INFRASTRUCTURE_TYPE_DEDICATED = "dedicated";

export const INFRASTRUCTURE_TYPE = Object.freeze({
  ALL: "all",
  GRID: INFRASTRUCTURE_TYPE_GRID,
  DEDICATED: INFRASTRUCTURE_TYPE_DEDICATED
});

export const SUBSCRIPTION_DEFAULTS = {
  storage: 5120,
  environments: 3,
  plan: "development"
};

export const PLATFORM_FEATURE_BLACKFIRE = "PLATFORM_FEATURE_BLACKFIRE";

export const CONFIGURE_RESOURCES_PROFILE_LABEL = {
  HIGHER_MEMORY: "Higher memory profile",
  HIGH_MEMORY: "High memory profile",
  BALANCED: "Balanced profile",
  LOW_MEMORY: "Low memory profile",
  HIGH_CPU: "High cpu profile"
} as const;

export const DIALOG_RESOURCE_PROFILE = {
  HIGHER_MEMORY: "Higher memory",
  HIGH_MEMORY: "High memory",
  BALANCED: "Balanced memory",
  LOW_MEMORY: "Low memory",
  HIGH_CPU: "High cpu"
} as const;

export const getResourceProfileLabel = (code: string) =>
  CONFIGURE_RESOURCES_PROFILE_LABEL[code] ?? code;
export const getResourceProfile = (code: string) =>
  DIALOG_RESOURCE_PROFILE[code] ?? code;

export const FLEXIBLE_PLAN_VALUE =
  config.CUSTOM_FLEXIBLE_PLAN_VALUE ?? "flexible";

export const DEFAULT_ORGANIZATION_COUNTRY = "FR";

// In invoices discounts come as line items with the following names]
// they don't follow a standard (yet) so we need to hardcode them here
export const DISCOUNT_LINE_ITEMS = [
  "standard_user_management_discount",
  "agency_discount",
  "special_partner",
  "enterprise_project",
  "invoice_account",
  "vendor_account",
  "custom_discount",
  "organization_inactivity_discount",
  "prepaid_discount",
  "voucher",
  "trial",
  "discount"
];

export const QUERY_PREVIOUS_PAGE_KEY = "previousPage";
export const QUERY_PREVIOUS_PAGE = "?" + QUERY_PREVIOUS_PAGE_KEY + "=";

export const QUERY_PROJECT_CREATION_KEY = "projectCreation";
export const QUERY_PROJECT_CREATION =
  "&" + QUERY_PROJECT_CREATION_KEY + "=true";

export const QUERY_PROJECT_BILLING_KEY = "projectBilling";
export const QUERY_PROJECT_BILLING = "&" + QUERY_PROJECT_BILLING_KEY + "=true";

export const DEFAULT_DEMO_REGION_ID = "eu-5.platform.sh";
export const DEFAULT_DEMO_REGION_CONTINENT = "Europe";

export const METADATA_TYPES = Object.freeze({
  app: "app",
  service: "service",
  worker: "worker",
  router: "router",
  task: "task"
});

export const getOperationTypeName = (activityType?: string): string => {
  switch (activityType) {
    case ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION:
      return "source operation";
    case ACTIVITY_TYPES.ENVIRONMENT.OPERATION:
      return "runtime operation";
    case ACTIVITY_TYPES.ENVIRONMENT.PUSH:
      return "push";
    case ACTIVITY_TYPES.ENVIRONMENT.MERGE:
      return "merge";
    case ACTIVITY_TYPES.ENVIRONMENT.SYNCHRONIZE:
      return "synchronize";
    case ACTIVITY_TYPES.ENVIRONMENT.BRANCH:
      return "branch";
    case ACTIVITY_TYPES.ENVIRONMENT.CRON:
      return "cron";
    case ACTIVITY_TYPES.ENVIRONMENT.BACKUP:
      return "backup";
    case ACTIVITY_TYPES.ENVIRONMENT.BACKUP_DELETE:
      return "backup delete";
    case ACTIVITY_TYPES.ENVIRONMENT.RESTORE:
      return "restore";
    case ACTIVITY_TYPES.ENVIRONMENT.DEPLOY:
      return "deploy";
    case ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY:
      return "redeploy";
    case ACTIVITY_TYPES.ENVIRONMENT.CERTIFICATE_RENEWAL:
      return "certificate renewal";
    case ACTIVITY_TYPES.ENVIRONMENT.ACTIVATE:
      return "activate";
    case ACTIVITY_TYPES.ENVIRONMENT.DEACTIVATE:
      return "deactivate";
    case ACTIVITY_TYPES.ENVIRONMENT.DELETE:
      return "delete";
    case ACTIVITY_TYPES.ENVIRONMENT.INITIALIZE:
      return "initialize";
    case ACTIVITY_TYPES.ENVIRONMENT.MERGE_PR:
      return "merge PR";
    case ACTIVITY_TYPES.ENVIRONMENT.PAUSE:
      return "pause";
    case ACTIVITY_TYPES.ENVIRONMENT.RESUME:
      return "resume";
    case ACTIVITY_TYPES.ENVIRONMENT.ALERT:
      return "alert";
    case ACTIVITY_TYPES.ENVIRONMENT.MAINTENANCE_REDEPLOY:
      return "maintenance redeploy";
    default:
      return "operation";
  }
};
export const ACTIVITY_TIME_FORMAT = "HH:mm MMM dd, yyyy";
